import React, { useMemo, useContext, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import PropTypes from 'prop-types';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  BusyBlocker,
  DefaultTemplate,
  CancelWizardDialog,
  StepList,
} from '@bingads-webui-react/wizard-ui-library';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withSiteMap, getRoutePathById } from '@bingads-webui-campaign-react/with-site-map';
import { withScope } from '@bingads-webui-react/scope-service';
import { setClarityTag } from '@bingads-webui-universal/clarity';

import { getFlowAssetGroupCreation } from '../step-utils';
import {
  StepConfigAssetGroupCreation,
  getStepListConfigAssetGroupCreation,
} from '../steps';
import { AppAdsStepFooter } from '../footer';
import { FLOWS, FLOWS_TO_FIRST_STEP_ASSET_GROUP } from '../step-constants';
import { WizardScenario } from '../shared/constants';

const AppAdsAssetGroupCreationPageInternal = ({
  siteMap, scope, perfMarker,
}) => {
  const routeToNavigateToWhenDone = getRoutePathById(siteMap, 'appads-asset-groups');
  const i18n = useContext(I18nContext);
  const instrumentation = useContext(InstrumentationContext);

  const flowType = FLOWS.WINDOWS_STORE_APPS;
  const initialStep = FLOWS_TO_FIRST_STEP_ASSET_GROUP[flowType];
  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild(`AppAdsWizard-${flowType}`)
  ), [flowType, perfMarker]);

  const stepListConfig = useMemo(() => getStepListConfigAssetGroupCreation({ i18n }), [i18n]);
  const config = useMemo(() => ({
    id: 'app-ads-wizard',
    initialStep,
    steps: StepConfigAssetGroupCreation,
    initialFlowFactors: { flow: flowType },
    initialWizardData: {
      flow: flowType,
      scenario: scope.values.assetGroupId ? WizardScenario.AssetGroupEdit : WizardScenario.AssetGroupCreation,
    },
    getFlowAssetGroupCreation,
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: () => scope.save(routeToNavigateToWhenDone),
    },
    attachStepActivities: true,
  }), [flowType, initialStep, routeToNavigateToWhenDone, scope]);

  useEffect(() => {
    setClarityTag({ AppAdsCampaigns: flowType });
  }, [flowType]);

  return (
    <PerformanceMarkerProvider
      name="AppAds"
      instrumentation={instrumentation}
      immediateDone={false}
    >
      <Scenario name={`AppAdsAssetGroupCreation-${flowType}`}>
        <Activity name="DefaultWizardActivity">
          <Wizard config={config}>
            <WizardPerfMarker wizardName="AppAdsWizard" wizardMarker={wizardPerfMarker} >
              <LayoutContainer template={DefaultTemplate}>
                <Layout type="StepList" component={StepList} config={stepListConfig} />
                <Layout type="StepContainer" component={StepContainer} />
                <Layout
                  type="Footer"
                  component={AppAdsStepFooter}
                />
                <Layout type="Blocker" component={BusyBlocker} loadingText="Processing" />
                <Layout type="CancelDialog" component={CancelWizardDialog} />
              </LayoutContainer>
            </WizardPerfMarker>
          </Wizard>
        </Activity>
      </Scenario>
    </PerformanceMarkerProvider>
  );
};

AppAdsAssetGroupCreationPageInternal.propTypes = {
  siteMap: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
    values: PropTypes.shape({
      assetGroupId: PropTypes.number,
    }),
  }).isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

export const AppAdsAssetGroupCreationPage = withScope(withSiteMap(AppAdsAssetGroupCreationPageInternal));
